import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  Typography,
  Stack,
} from '@mui/material';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { uploads } from '@prisma/client';
import { Close, ArrowBack, ArrowForward } from '@mui/icons-material';
import useIsMobile from '../../../hooks/useIsMobile';
import { useState, useRef, useCallback } from 'react';
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch';

type PhotoItem = uploads | string;

function getPhotoUrl(photo: PhotoItem): string {
  return typeof photo === 'string' ? photo : photo.url;
}

function getPhotoKey(photo: PhotoItem, index: number): string {
  return typeof photo === 'string' ? `photo-${index}` : photo.id;
}

interface Props {
  photos: PhotoItem[];
  initialPhotoIndex?: number;
}

const PhotosModal = NiceModal.create<Props>((props) => {
  const modal = useModal();
  const { photos, initialPhotoIndex = 0 } = props;
  const isMobile = useIsMobile();
  const [isGridView, setIsGridView] = useState(
    props.initialPhotoIndex === undefined
  );
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(initialPhotoIndex);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handlePhotoClick = (index: number) => {
    setCurrentPhotoIndex(index);
    setIsGridView(false);
  };

  const resetZoom = useCallback(() => {
    transformRef.current?.resetTransform(0);
    setIsZoomed(false);
  }, []);

  const handlePrevious = useCallback(() => {
    resetZoom();
    setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  }, [photos.length, resetZoom]);

  const handleNext = useCallback(() => {
    resetZoom();
    setCurrentPhotoIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  }, [photos.length, resetZoom]);

  const handleTransform = useCallback(
    (ref: ReactZoomPanPinchRef) => {
      const scale = ref.state.scale;
      setIsZoomed(scale > 1.05);
    },
    []
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isZoomed || e.touches.length > 1) return;
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    },
    [isZoomed]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (isZoomed || !touchStartRef.current) return;

      const touchEnd = e.changedTouches[0];
      const diffX = touchStartRef.current.x - touchEnd.clientX;
      const diffY = Math.abs(touchStartRef.current.y - touchEnd.clientY);

      // Only swipe if horizontal movement > 50px and more horizontal than vertical
      if (Math.abs(diffX) > 50 && diffY < Math.abs(diffX)) {
        if (diffX > 0) {
          handleNext();
        } else {
          handlePrevious();
        }
      }

      touchStartRef.current = null;
    },
    [isZoomed, handleNext, handlePrevious]
  );

  const currentUrl = getPhotoUrl(photos[currentPhotoIndex]);

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.remove()}
      maxWidth="lg"
      fullScreen={true}
    >
      <DialogTitle
        sx={{
          py: 2,
          maxHeight: 50,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {!isGridView && (
              <IconButton
                onClick={() => {
                  resetZoom();
                  setIsGridView(true);
                }}
              >
                <ArrowBack />
              </IconButton>
            )}
            <Typography variant="body1">
              {isGridView
                ? 'Photos'
                : `Photo ${currentPhotoIndex + 1} of ${photos.length}`}
            </Typography>
          </Stack>
          <IconButton onClick={() => modal.remove()}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: isMobile ? 0 : undefined,
          height: 'calc(100vh - 120px)',
          overflow: 'hidden',
          '&.MuiDialogContent-root': {
            padding: isMobile ? 0 : undefined,
          },
        }}
      >
        {isGridView ? (
          <ImageList
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
            }}
            cols={isMobile ? 1 : 2}
          >
            {photos.map((item, index) => {
              const url = getPhotoUrl(item);
              return (
                <ImageListItem
                  key={getPhotoKey(item, index)}
                  onClick={() => handlePhotoClick(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  <img
                    srcSet={`${url}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                    src={`${url}?w=164&h=164&fit=crop&auto=format`}
                    loading="lazy"
                  />
                </ImageListItem>
              );
            })}
          </ImageList>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ width: '100%', height: '100%' }}
          >
            {!isMobile && (
              <IconButton
                onClick={handlePrevious}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.dark' },
                }}
              >
                <ArrowBack color="inherit" sx={{ color: 'white' }} />
              </IconButton>
            )}
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '90vh',
                touchAction: 'none',
              }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <TransformWrapper
                ref={transformRef}
                initialScale={1}
                minScale={1}
                maxScale={4}
                doubleClick={{ mode: 'toggle', step: 2 }}
                panning={{ disabled: !isZoomed }}
                onTransformed={handleTransform}
                key={currentPhotoIndex}
              >
                <TransformComponent
                  wrapperStyle={{
                    width: '100%',
                    height: '100%',
                  }}
                  contentStyle={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={currentUrl}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '90vh',
                      objectFit: 'contain',
                    }}
                    alt=""
                    draggable={false}
                  />
                </TransformComponent>
              </TransformWrapper>
            </div>
            {!isMobile && (
              <IconButton
                onClick={handleNext}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.dark' },
                }}
              >
                <ArrowForward color="inherit" sx={{ color: 'white' }} />
              </IconButton>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default PhotosModal;
